import type { Directive, DirectiveBinding } from 'vue'
import { useAuthStore } from '@/stores/auth'

/**
 * SDD-0024-AUTH: Directiva global `v-permission`.
 * 
 * Uso:
 *  - v-permission="'billing:create_invoice'"
 *  - v-permission="['billing.read', 'billing.write']" (al menos uno)
 *  - v-permission.all="['billing.read', 'billing.write']" (todos requeridos)
 *  - v-permission.disable="'billing:anular'" (deshabilita visualmente en vez de remover del DOM)
 */
const commentMap = new WeakMap<HTMLElement, Comment>()

function checkPermission(binding: DirectiveBinding): boolean {
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) return false

  const value = binding.value
  if (!value) return true

  if (Array.isArray(value)) {
    if (binding.modifiers.all) {
      return value.every(p => authStore.hasPermission(p))
    }
    return value.some(p => authStore.hasPermission(p))
  }

  if (typeof value === 'string') {
    return authStore.hasPermission(value)
  }

  return true
}

export const vPermission: Directive<HTMLElement> = {
  mounted(el, binding) {
    const hasAccess = checkPermission(binding)
    if (!hasAccess) {
      if (binding.modifiers.disable) {
        el.setAttribute('disabled', 'true')
        el.classList.add('btn-disabled', 'opacity-50', 'cursor-not-allowed', 'pointer-events-none')
      } else {
        const comment = document.createComment('v-permission: access denied')
        commentMap.set(el, comment)
        el.parentNode?.replaceChild(comment, el)
      }
    }
  },

  updated(el, binding) {
    const hasAccess = checkPermission(binding)
    if (binding.modifiers.disable) {
      if (hasAccess) {
        el.removeAttribute('disabled')
        el.classList.remove('btn-disabled', 'opacity-50', 'cursor-not-allowed', 'pointer-events-none')
      } else {
        el.setAttribute('disabled', 'true')
        el.classList.add('btn-disabled', 'opacity-50', 'cursor-not-allowed', 'pointer-events-none')
      }
    } else {
      const comment = commentMap.get(el)
      if (!hasAccess && el.parentNode) {
        const newComment = comment || document.createComment('v-permission: access denied')
        commentMap.set(el, newComment)
        el.parentNode.replaceChild(newComment, el)
      } else if (hasAccess && comment && comment.parentNode) {
        comment.parentNode.replaceChild(el, comment)
        commentMap.delete(el)
      }
    }
  }
}

export default vPermission
